import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Avatar, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useConfirm } from 'material-ui-confirm';

import MenuVisibility from 'views/MenuCategory/components/MenuVisibility';
import {
  dishFormFrom,
  editedDish,
  emptyDishForm,
  groupsInOrder,
  newDishPayload,
  withGroup,
  withoutGroup,
} from '../menuEditorPayloads';
import EditorDrawer from './EditorDrawer';
import DishDetailsForm, { dishFormErrors } from './DishDetailsForm';
import DishLabelsForm, { dishWithLabels, labelsFormFrom } from './DishLabelsForm';
import ChoiceGroupsField from './ChoiceGroupsField';

const TABS = [
  { value: 'details', label: 'Details' },
  { value: 'choices', label: 'Choices' },
  { value: 'visibility', label: 'Visibility' },
  { value: 'labels', label: 'Tags & kitchen' },
];

/** Keeps the choice groups attached to each size, which are edited on another tab, when saving details. */
const sizesKeepingGroups = (formSizes, storedSizes) => {
  const stored = new Map((storedSizes || []).map((size) => [size.id, size]));
  return formSizes.map((size) => ({ ...size, groups: stored.has(size.id) ? stored.get(size.id).groups || [] : size.groups || [] }));
};

// Size groups are edited on the Choices tab, and a typed "10" equals a stored 10.
const comparableForm = (form) =>
  JSON.stringify({
    ...form,
    price: String(form.price ?? ''),
    minQuantity: String(form.minQuantity ?? ''),
    sizes: form.sizes.map((size) => ({ ...size, groups: undefined, price: String(size.price ?? '') })),
  });

const DishEditor = ({
  open,
  mode,
  dish,
  category,
  menuId,
  groups,
  menu,
  printCategoryNames,
  currencySymbol,
  image,
  actions,
  onClose,
  onCreated,
  onUploadImage,
  onDuplicate,
  onMove,
  onDelete,
}) => {
  const confirm = useConfirm();
  const isNew = mode === 'new';
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState(emptyDishForm());
  const [labels, setLabels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const dishId = dish ? dish.id : null;
  // Seed the forms when a different dish opens; saves elsewhere must not wipe what is being typed.
  useEffect(() => {
    setForm(dish ? dishFormFrom(dish) : emptyDishForm());
    setLabels(dish ? labelsFormFrom(dish) : null);
    setShowErrors(false);
    setTab('details');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishId, isNew, open]);

  const errors = dishFormErrors(form, { isNew, printCategoryNames });
  const hasErrors = Object.keys(errors).length > 0;

  const detailsDirty = useMemo(
    () => comparableForm(dish ? dishFormFrom(dish) : emptyDishForm()) !== comparableForm(form),
    [dish, form]
  );
  const labelsDirty = useMemo(
    () => !!(dish && labels && JSON.stringify(labelsFormFrom(dish)) !== JSON.stringify(labels)),
    [dish, labels]
  );

  const close = async () => {
    if (detailsDirty || labelsDirty) {
      try {
        await confirm({ title: 'Discard changes?', description: 'You have changes that have not been saved.', confirmationText: 'Discard' });
      } catch (cancelled) {
        return;
      }
    }
    onClose();
  };

  const run = async (work) => {
    setSaving(true);
    try {
      return await work();
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    if (isNew) {
      const result = await run(() => actions.createDish(newDishPayload(form, menuId, category.id)));
      if (result) {
        onCreated(result, category.id);
      }
      return;
    }
    const next = editedDish(dish, { ...form, sizes: sizesKeepingGroups(form.sizes, dish.sizes) }, menuId, category.id);
    const saved = await run(() => actions.updateDish(next));
    if (saved) {
      setForm(dishFormFrom(next));
    }
  };

  const saveLabels = async () => {
    const next = { ...dishWithLabels(dish, labels), categoryId: category.id, menuId };
    await run(async () => {
      const saved = await actions.updateDish(next);
      if (saved && labels.promotionTags.length > 0) {
        await actions.addMenuPromotionTags(labels.promotionTags);
      }
      return saved;
    });
  };

  const saveGroups = (patch) => run(() => actions.updateDish({ ...dish, ...patch, categoryId: category.id, menuId }));
  const saveSizeGroups = (sizeId, groupRefs) =>
    saveGroups({ sizes: (dish.sizes || []).map((size) => (size.id === sizeId ? { ...size, groups: groupRefs } : size)) });

  const footer =
    tab === 'details' ? (
      <>
        <Button color="inherit" onClick={close}>
          {isNew ? 'Cancel' : 'Close'}
        </Button>
        <Button variant="contained" disableElevation onClick={saveDetails} disabled={saving || (!isNew && !detailsDirty)}>
          {isNew ? 'Create dish' : 'Save changes'}
        </Button>
      </>
    ) : tab === 'labels' ? (
      <>
        <Button color="inherit" onClick={close}>
          Close
        </Button>
        <Button variant="contained" disableElevation onClick={saveLabels} disabled={saving || !labelsDirty}>
          Save changes
        </Button>
      </>
    ) : null;

  const media = !isNew && (
    <Tooltip title={image ? 'Replace photo' : 'Add photo'}>
      <Box component="button" type="button" onClick={onUploadImage} aria-label={image ? 'Replace photo' : 'Add photo'} sx={{ p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}>
        <Avatar variant="rounded" src={image || undefined} alt="" sx={{ width: 64, height: 64, borderRadius: 2, bgcolor: 'grey.100', color: 'grey.400' }}>
          <RestaurantIcon />
        </Avatar>
      </Box>
    </Tooltip>
  );

  const headerActions = !isNew && (
    <>
      <Tooltip title="Duplicate">
        <IconButton aria-label="Duplicate dish" onClick={onDuplicate}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Move to another category">
        <IconButton aria-label="Move dish to another category" onClick={onMove}>
          <DriveFileMoveOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton aria-label="Delete dish" onClick={onDelete}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const missing = !isNew && !dish;

  return (
    <EditorDrawer
      open={open}
      onClose={close}
      title={isNew ? 'New dish' : (dish && dish.name) || 'Dish'}
      subtitle={category ? `${isNew ? 'Adding to' : 'In'} ${category.name}` : null}
      media={media}
      actions={headerActions}
      tabs={isNew || missing ? null : TABS}
      tab={tab}
      onTabChange={setTab}
      footer={missing ? null : footer}
    >
      {missing && <Alert severity="info">This dish is no longer on the menu.</Alert>}

      {!missing && tab === 'details' && (
        <DishDetailsForm
          form={form}
          onChange={setForm}
          errors={errors}
          isNew={isNew}
          printCategoryNames={printCategoryNames}
          currencySymbol={currencySymbol}
          showErrors={showErrors}
        />
      )}

      {!missing && tab === 'choices' && dish && (
        <Box>
          <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
            Changes on this tab save straight away.
          </Alert>
          <ChoiceGroupsField
            title="Choice groups for this dish"
            description="Customers choose from these when ordering. Drag to set the order."
            attached={dish.groups}
            groups={groups}
            busy={saving}
            dragType={`editor-dish-groups-${dish.id}`}
            onAdd={(groupId) => saveGroups({ groups: withGroup(dish.groups, groupId) })}
            onRemove={(groupId) => saveGroups({ groups: withoutGroup(dish.groups, groupId) })}
            onReorder={(from, to) => saveGroups({ groups: groupsInOrder(dish.groups, from, to) })}
          />
          {(dish.sizes || []).map((size) => (
            <ChoiceGroupsField
              key={size.id}
              title={`Choice groups for size “${size.name || 'Unnamed'}”`}
              description="Only offered when this size is picked."
              attached={size.groups}
              groups={groups}
              busy={saving}
              onAdd={(groupId) => saveSizeGroups(size.id, withGroup(size.groups, groupId))}
              onRemove={(groupId) => saveSizeGroups(size.id, withoutGroup(size.groups, groupId))}
            />
          ))}
          {detailsDirty && (dish.sizes || []).length !== form.sizes.length && (
            <Typography variant="body2" color="text.secondary">
              Save the sizes on the Details tab to attach groups to new sizes.
            </Typography>
          )}
        </Box>
      )}

      {!missing && tab === 'visibility' && dish && (
        <Box>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Changes on this tab save straight away.
          </Alert>
          <MenuVisibility visibilityObj={dish} menuItem={{ ...dish, categoryId: category.id, menuId }} />
        </Box>
      )}

      {!missing && tab === 'labels' && dish && labels && (
        <DishLabelsForm
          form={labels}
          onChange={setLabels}
          menuPromotionTags={(menu && menu.promotionTags) || []}
          printCategoryNames={printCategoryNames}
        />
      )}
    </EditorDrawer>
  );
};

DishEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['new', 'edit']),
  dish: PropTypes.object,
  category: PropTypes.object,
  menuId: PropTypes.string.isRequired,
  groups: PropTypes.array.isRequired,
  menu: PropTypes.object,
  printCategoryNames: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  image: PropTypes.string,
  actions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  onUploadImage: PropTypes.func,
  onDuplicate: PropTypes.func,
  onMove: PropTypes.func,
  onDelete: PropTypes.func,
};

export default DishEditor;
